export type CatalogCsvEntityKind = 'categories' | 'brands' | 'suppliers' | 'products';

export type CsvFlowMode = 'catalog' | 'stock' | 'customers';

export type CsvColumnRequirement = 'obligatorio' | 'opcional' | 'obligatorio_alta';

export type CsvColumnHint = {
  column: string;
  requirement: CsvColumnRequirement;
  /** Cómo escribir el valor en Excel/CSV (decimales, enteros). */
  formatNote?: string;
};

/** Guía corta: montos y precios con convención es-AR (misma regla que el formulario en app). */
export const CSV_DECIMAL_FORMAT_NOTE =
  'Usá la coma para decimales: 1500,50. Si el número tiene centavos, el punto separa miles: 1.500,50. Sin centavos podés escribir 1500. También aceptamos 1500.50 (punto decimal internacional). No uses letras ni el símbolo $ en la celda.';

/** Guía corta: cantidades enteras en stock CSV. */
export const CSV_INTEGER_FORMAT_NOTE =
  'Solo números enteros (0, 1, 24…). Sin coma, punto ni letras.';

/** Guía corta: porcentajes en maestros CSV (margen, markup). */
export const CSV_PERCENT_FORMAT_NOTE =
  'Número con coma o punto decimal (ej. 30 o 25,5). Sin el signo %.';

/** Guía corta: columna `cantidad` en el import de stock (ingreso aditivo). */
export const CSV_STOCK_CANTIDAD_NOTE =
  'Es lo que ingresás, no el total. Se SUMA al stock de esta sucursal. Entero de 1 o más (salvo kg/l, que admiten decimales).';

/** Guía corta: columna `motivo` en el import de stock. */
export const CSV_STOCK_MOTIVO_NOTE =
  'recepcion (compra), reposicion o alta_inicial. Vacío = alta_inicial si no había stock, si no reposicion.';

/** Guía corta: columna `proveedor` en el import de stock. */
export const CSV_STOCK_PROVEEDOR_NOTE =
  'Un proveedor que ya tengas: nombre, código o id. Vacío = sin proveedor.';

/** Guía corta: columna `vencimiento` en el import de stock. */
export const CSV_STOCK_VENCIMIENTO_NOTE = 'Fecha AAAA-MM-DD. Opcional.';

export const CSV_CATEGORY_NAME_NOTE =
  'El nombre exacto de una categoría del rubro. No inventes nombres. Si falta, pedila desde Categorías.';

export const CSV_CATEGORY_ACTIVO_NOTE =
  'si o no. Es lo único que cambia el import: prender u ocultar. Vacío = no toca esa fila.';

export const CSV_PRODUCT_CATEGORY_NOTE =
  'Una de las que ves en Categorías. Nombre exacto, o el categoria_id del export. No pongas un nombre inventado.';

export const CSV_PRODUCT_TIPO_NOTE = 'fisico o servicio. Vacío en un producto que ya existe = no cambia.';

export const CSV_ACTIVO_NOTE = 'si o no. Vacío = no cambia el estado.';

/** Guía: códigos de barras EAN/UPC o alfanuméricos + Excel que corta dígitos. */
export const CSV_BARCODE_FORMAT_NOTE =
  'Letras y números. Si es solo dígitos (EAN/UPC), tiene que tener 8, 12, 13 o 14. En update, si viene inválido se conserva el de la base y queda aviso. Cuidado con Excel: a veces borra un cero o lo pasa a notación científica; mejor texto o exportá de nuevo desde Vuelto.';

export const CSV_STOCK_SNAPSHOT_IMPORT_MESSAGE =
  'Ese archivo es el stock actual, no un ingreso. Si lo importás, la cantidad se suma y duplicás mercadería. Usá “Plantilla de ingreso”.';

export function formatCsvColumnRequirement(requirement: CsvColumnRequirement): string {
  switch (requirement) {
    case 'obligatorio':
      return 'Obligatorio';
    case 'obligatorio_alta':
      return 'Obligatorio en alta';
    case 'opcional':
      return 'Opcional';
  }
}

export function getCsvHeroSubtitle(mode: CsvFlowMode, catalogEntity?: CatalogCsvEntityKind): string {
  if (mode === 'stock') {
    return 'La cantidad se suma al stock de esta sucursal. El export es una foto: no lo vuelvas a importar.';
  }
  if (mode === 'customers') {
    return 'Teléfono o email identifica al cliente. La importación no modifica saldos, deudas ni límites.';
  }
  switch (catalogEntity) {
    case 'products':
      return 'SKU nuevo agrega. El mismo SKU actualiza. La categoría tiene que ser una del rubro.';
    case 'categories':
      return 'Las categorías vienen del rubro. Acá solo las prendés o las ocultás. No se crean nombres nuevos.';
    case 'brands':
      return 'Exportá, editá e importá. Nombre nuevo crea la marca. El mismo nombre o id la actualiza.';
    case 'suppliers':
      return 'Exportá, editá e importá. Nombre nuevo crea el proveedor. El mismo nombre o id lo actualiza.';
    default:
      return 'Revisá la previa antes de confirmar.';
  }
}

export function shouldOfferCsvTemplate(mode: CsvFlowMode, catalogEntity?: CatalogCsvEntityKind): boolean {
  if (mode === 'stock' || mode === 'customers') return true;
  return catalogEntity !== 'categories';
}

export function getCsvTemplateButtonLabel(mode: CsvFlowMode): string {
  if (mode === 'stock') return 'Plantilla de ingreso';
  if (mode === 'customers') return 'Plantilla de clientes';
  return 'Plantilla con ejemplo';
}

export function getCsvExportButtonLabel(mode: CsvFlowMode, catalogEntity?: CatalogCsvEntityKind): string {
  if (mode === 'stock') return 'Exportar stock actual';
  if (mode === 'customers') return 'Exportar clientes';
  if (catalogEntity === 'categories') return 'Exportar las del rubro';
  return 'Exportar';
}

/** Resumen corto para el modal (sin párrafos largos). */
export function getCsvColumnHints(mode: CsvFlowMode, catalogEntity?: CatalogCsvEntityKind): CsvColumnHint[] {
  if (mode === 'customers') {
    return [
      { column: 'nombre', requirement: 'obligatorio' },
      { column: 'telefono', requirement: 'opcional', formatNote: 'Completá teléfono o email. Conservá el prefijo de país si lo usás.' },
      { column: 'email', requirement: 'opcional', formatNote: 'Completá email o teléfono.' },
      { column: 'cuit', requirement: 'opcional' },
      { column: 'direccion', requirement: 'opcional' },
      { column: 'categoria_fiscal', requirement: 'opcional' },
      { column: 'notas', requirement: 'opcional' },
      { column: 'dias_pago', requirement: 'opcional', formatNote: 'Número entero mayor o igual a 0.' },
    ];
  }
  if (mode === 'stock') {
    return [
      { column: 'sku', requirement: 'obligatorio' },
      {
        column: 'cantidad',
        requirement: 'obligatorio',
        formatNote: CSV_STOCK_CANTIDAD_NOTE,
      },
      {
        column: 'costo_unitario',
        requirement: 'opcional',
        formatNote: CSV_DECIMAL_FORMAT_NOTE,
      },
      { column: 'motivo', requirement: 'opcional', formatNote: CSV_STOCK_MOTIVO_NOTE },
      { column: 'proveedor', requirement: 'opcional', formatNote: CSV_STOCK_PROVEEDOR_NOTE },
      {
        column: 'stock_minimo',
        requirement: 'opcional',
        formatNote: CSV_INTEGER_FORMAT_NOTE,
      },
      { column: 'vencimiento', requirement: 'opcional', formatNote: CSV_STOCK_VENCIMIENTO_NOTE },
      { column: 'lote', requirement: 'opcional' },
    ];
  }

  switch (catalogEntity) {
    case 'categories':
      return [
        { column: 'categoria_id', requirement: 'opcional' },
        { column: 'nombre', requirement: 'obligatorio', formatNote: CSV_CATEGORY_NAME_NOTE },
        { column: 'activo', requirement: 'obligatorio', formatNote: CSV_CATEGORY_ACTIVO_NOTE },
      ];
    case 'brands':
      return [
        { column: 'nombre', requirement: 'obligatorio' },
        { column: 'codigo', requirement: 'opcional' },
        { column: 'sitio_web', requirement: 'opcional' },
        { column: 'markup_default', requirement: 'opcional', formatNote: CSV_PERCENT_FORMAT_NOTE },
        { column: 'activo', requirement: 'opcional', formatNote: CSV_ACTIVO_NOTE },
      ];
    case 'suppliers':
      return [
        { column: 'nombre', requirement: 'obligatorio' },
        { column: 'codigo', requirement: 'opcional' },
        { column: 'cuit', requirement: 'opcional' },
        { column: 'activo', requirement: 'opcional', formatNote: CSV_ACTIVO_NOTE },
        {
          column: 'monto_minimo_pedido',
          requirement: 'opcional',
          formatNote: CSV_DECIMAL_FORMAT_NOTE,
        },
      ];
    case 'products':
    default:
      return [
        { column: 'sku', requirement: 'obligatorio' },
        { column: 'nombre', requirement: 'obligatorio_alta' },
        {
          column: 'precio',
          requirement: 'obligatorio_alta',
          formatNote: CSV_DECIMAL_FORMAT_NOTE,
        },
        { column: 'codigo_barras', requirement: 'opcional', formatNote: CSV_BARCODE_FORMAT_NOTE },
        { column: 'tipo', requirement: 'opcional', formatNote: CSV_PRODUCT_TIPO_NOTE },
        {
          column: 'categoria',
          requirement: 'opcional',
          formatNote: CSV_PRODUCT_CATEGORY_NOTE,
        },
        { column: 'categoria_id', requirement: 'opcional' },
        { column: 'marca_id', requirement: 'opcional' },
        { column: 'marca', requirement: 'opcional' },
        { column: 'proveedor_preferido_id', requirement: 'opcional' },
        { column: 'proveedor_preferido', requirement: 'opcional' },
        { column: 'codigo_proveedor', requirement: 'opcional' },
        { column: 'notas', requirement: 'opcional' },
        { column: 'activo', requirement: 'opcional', formatNote: CSV_ACTIVO_NOTE },
        // #712: Columnas de variante (opcionales).
        { column: 'variante_sku', requirement: 'opcional', formatNote: 'SKU único de la variante. Vacío = la variante se crea sin SKU.' },
        { column: 'variante_codigo_barras', requirement: 'opcional', formatNote: 'Código de barras único de la variante.' },
        { column: 'variante_precio', requirement: 'opcional', formatNote: CSV_DECIMAL_FORMAT_NOTE + ' Vacío = hereda el precio del parent.' },
        { column: 'variante_eje_1', requirement: 'opcional', formatNote: 'Nombre del eje (ej: Talle, Color).' },
        { column: 'variante_valor_1', requirement: 'opcional', formatNote: 'Valor del eje 1 (ej: S, Negro).' },
        { column: 'variante_eje_2', requirement: 'opcional' },
        { column: 'variante_valor_2', requirement: 'opcional' },
        { column: 'variante_eje_3', requirement: 'opcional' },
        { column: 'variante_valor_3', requirement: 'opcional' },
      ];
  }
}

export function getCsvGuideNote(mode: CsvFlowMode, catalogEntity?: CatalogCsvEntityKind): string {
  if (mode === 'stock') {
    return 'Usá la plantilla de ingreso. El export de stock actual es para mirar, no para volver a subir.';
  }
  if (mode === 'customers') {
    return 'Usá la plantilla o exportá tus clientes. Cada fila necesita teléfono o email; los saldos y límites quedan intactos.';
  }
  if (catalogEntity === 'products') {
    return 'En categoría poné una de las que ves en Categorías, nombre exacto. Celda vacía en un SKU que ya existe = no cambia ese campo. Para variantes, repetí el SKU del parent en cada fila y completá variante_eje_1/variante_valor_1 (hasta 3 ejes).';
  }
  if (catalogEntity === 'categories') {
    return 'Exportá las del rubro, cambiá si/no en activo e importá. No agregues filas ni cambies los nombres. Si falta una, pedila desde Categorías.';
  }
  if (catalogEntity === 'brands') {
    return 'La plantilla trae ejemplos: borralos o reemplazalos. Vacío en activo = no cambia si ya existía.';
  }
  if (catalogEntity === 'suppliers') {
    return 'La plantilla trae ejemplos: borralos o reemplazalos. Vacío en activo = no cambia si ya existía.';
  }
  return 'La plantilla incluye una fila de ejemplo. Borrala o reemplazala antes de importar.';
}

export const CSV_FIRST_TIME_STEPS = [
  'Marcas → Proveedores → Productos. Las categorías ya están: activálas en Categorías. Stock, al final, en Inventario.',
];

/** Acción visible en la vista previa: Alta, Edición, Error (o Igual si se listan filas sin cambio). */
export function formatPreviewAccion(status: string, action: string): string {
  if (status === 'error') return 'Error';
  if (status === 'skip' || action === 'omitir') return 'Igual';
  if (action === 'crear') return 'Alta';
  if (action === 'actualizar') return 'Edición';
  switch (action) {
    case 'validar':
      return 'Error';
    case 'ingreso':
      return 'Ingreso';
    case 'ajustar':
      return 'Stock';
    case 'minimo':
      return 'Mínimo';
    case 'encabezado':
    case 'límite':
    case 'sucursal':
      return 'Error';
    default:
      return action;
  }
}

export function formatPreviewDetail(
  status: string,
  detail: string,
  message?: string
): string {
  if (status === 'skip') return detail;
  const hint = message?.trim();
  if (!hint) {
    if (detail.length <= 48) return detail;
    return `${detail.slice(0, 45)}…`;
  }
  if (detail.length <= 36) return `${detail} · ${hint}`;
  return `${detail.slice(0, 33)}… · ${hint}`;
}

function pluralEs(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

export type CsvImportDoneCounts = {
  applied: number;
  skipped: number;
  failed: number;
  altas: number;
  ediciones: number;
};

export type CsvImportDoneLineTone = 'default' | 'muted' | 'error' | 'success';

export type CsvImportDonePresentation = {
  headline: string;
  detail: string;
  lines: Array<{ text: string; tone: CsvImportDoneLineTone }>;
};

export function buildImportDonePresentation(
  mode: CsvFlowMode,
  counts: CsvImportDoneCounts
): CsvImportDonePresentation {
  const { applied, skipped, failed, altas, ediciones } = counts;
  const hasApplied = applied > 0;
  const hasFailed = failed > 0;

  let headline = 'Importación exitosa';
  if (!hasApplied && hasFailed) {
    headline = 'No se aplicaron cambios';
  } else if (hasApplied && hasFailed) {
    headline = 'Importación parcial';
  } else if (!hasApplied && !hasFailed && skipped > 0) {
    headline = 'Sin cambios para aplicar';
  }

  let detail = 'Los datos quedaron guardados en el dispositivo.';
  if (hasApplied && !hasFailed) {
    detail = 'La carga del CSV se completó correctamente.';
  } else if (hasApplied && hasFailed) {
    detail = 'Algunas filas se aplicaron; otras fallaron al guardar.';
  } else if (hasFailed) {
    detail = 'Corregí el archivo o descargá el detalle de errores.';
  } else if (skipped > 0) {
    detail = 'Todas las filas ya estaban actualizadas. No hubo cambios para aplicar.';
  }

  const lines: CsvImportDonePresentation['lines'] = [];

  if (mode === 'stock') {
    if (applied > 0) {
      lines.push({
        text: `${applied} ${pluralEs(applied, 'ingreso aplicado', 'ingresos aplicados')}`,
        tone: 'success',
      });
    }
  } else {
    if (altas > 0) {
      lines.push({
        text: `${altas} ${pluralEs(altas, 'alta', 'altas')}`,
        tone: 'success',
      });
    }
    if (ediciones > 0) {
      lines.push({
        text: `${ediciones} ${pluralEs(ediciones, 'edición', 'ediciones')}`,
        tone: 'success',
      });
    }
  }

  if (skipped > 0) {
    lines.push({
      text: `${skipped} ${pluralEs(skipped, 'fila igual omitida', 'filas iguales omitidas')}`,
      tone: 'muted',
    });
  }

  if (failed > 0) {
    lines.push({
      text: `${failed} ${pluralEs(failed, 'error al aplicar', 'errores al aplicar')}`,
      tone: 'error',
    });
  }

  return { headline, detail, lines };
}
